import { useState } from "react";
import { Sparkles, Loader2, RefreshCw, Trophy, Lightbulb, ArrowRight } from "lucide-react";
import { generateImpactReport, type AIImpactReport } from "@/lib/ai-engine";
import { formatINR } from "@/data/sample";
import { Link } from "@tanstack/react-router";

interface Props {
  totalDonated: number;
  donationCount: number;
  institutionCount: number;
}

export function AIImpactReportCard({ totalDonated, donationCount, institutionCount }: Props) {
  const [report, setReport] = useState<AIImpactReport | null>(null);
  const [loading, setLoading] = useState(false);

  async function generate() {
    setLoading(true);
    setReport(null);
    try {
      const r = await generateImpactReport(totalDonated, donationCount, institutionCount);
      setReport(r);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="rounded-2xl border border-amber-200 overflow-hidden shadow-[var(--shadow-card)]">
      <div
        className="flex items-center gap-3 px-5 py-4"
        style={{ background: "linear-gradient(135deg, #7c2d12 0%, #b45309 100%)" }}
      >
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/20">
          <Trophy className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-white text-sm">AI Impact Report</p>
          <p className="text-[11px] text-white/70">Generated from your actual donation data</p>
        </div>
        <button
          onClick={generate}
          disabled={loading}
          className="flex items-center gap-1.5 rounded-xl bg-white/20 hover:bg-white/30 px-3 py-1.5 text-xs font-semibold text-white transition disabled:opacity-60"
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
          {loading ? "Generating..." : report ? "Regenerate" : "Generate"}
        </button>
      </div>

      <div className="bg-white p-5">
        {!report && !loading && (
          <div className="flex flex-col items-center py-8 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[var(--maroon-deep)]">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
            <p className="font-semibold text-[var(--fg)]">
              Your AI Impact Report is ready to generate
            </p>
            <p className="mt-1.5 max-w-xs text-sm text-foreground/60">
              Our AI analyses your {donationCount} donations worth {formatINR(totalDonated)} to
              quantify your real-world impact.
            </p>
            <button
              onClick={generate}
              className="mt-5 flex items-center gap-2 rounded-xl bg-[var(--maroon)] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[var(--maroon-deep)] transition shadow-sm"
            >
              <Sparkles className="h-4 w-4" /> Generate My Impact Report
            </button>
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center py-8 gap-4">
            <div className="relative">
              <div className="h-14 w-14 rounded-full border-4 border-[var(--maroon)]/20" />
              <Loader2 className="absolute inset-0 m-auto h-8 w-8 animate-spin text-[var(--maroon)]" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-[var(--fg)]">AI is analysing your impact...</p>
              <p className="mt-1 text-sm text-foreground/60">
                Crunching donation data, computing tax savings, calculating animal lives touched
              </p>
            </div>
          </div>
        )}

        {report && !loading && (
          <div className="space-y-4">
            <div className="rounded-xl bg-[var(--maroon)]/8 p-4">
              <p className="text-lg font-bold text-[var(--maroon)] leading-snug">
                {report.headline}
              </p>
              <p className="mt-1 text-sm text-foreground/70">{report.summary}</p>
            </div>

            <div>
              <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-foreground/60">
                Your Achievements
              </p>
              <ul className="space-y-2">
                {report.achievements.map((a, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-[var(--fg)]">
                    <span className="shrink-0 text-base leading-5">
                      {["🐄", "🐦", "🐕", "📋", "🏆"][i] ?? "•"}
                    </span>
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 uppercase tracking-wider">
                <Trophy className="h-3.5 w-3.5" /> Next Milestone
              </p>
              <p className="mt-1 text-sm text-[var(--fg)]">{report.nextGoal}</p>
            </div>

            <div className="rounded-xl border border-blue-200 bg-blue-50 p-3.5">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 uppercase tracking-wider">
                <Lightbulb className="h-3.5 w-3.5" /> AI Recommendation
              </p>
              <p className="mt-1 text-sm text-blue-800">{report.tip}</p>
            </div>

            <div className="flex gap-3 pt-1">
              <Link
                to="/causes"
                className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-[var(--maroon)] py-2.5 text-sm font-semibold text-white hover:bg-[var(--maroon-deep)] transition"
              >
                Give more impact <ArrowRight className="h-4 w-4" />
              </Link>
              <button
                onClick={generate}
                className="flex items-center gap-1.5 rounded-xl border border-border/50 px-4 py-2.5 text-sm text-foreground/70 hover:bg-[var(--bg)] transition"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
